import { useState } from 'react'
import { useAppForm } from '@/hooks/app-form'
import { useApiMutation, useApiQuery } from '@/api/ApiQueryServiceProvider'
import {
  AddResourceTypeFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type AddResourceTypeFormData
} from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import {
  renderTextControl,
  renderSelectControl,
  renderTextareaControl,
  renderButtonControl,
  type ControlConfig
} from '../../formRenderFunctions'
import { PermissionsToggleControl, OrderedMultiSelect, LiveUriPreview } from '@/components/rema/shared'
import { RemaEnvironmentBar } from '@/components/rema/RemaEnvironmentBar'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Settings2 } from 'lucide-react'
import { buildResourceTypeUri, getTenantId, type RemaEnvironment } from '@/hooks/useRemaUriBuilder'
import { sanitizeResourceTypeName } from '@/lib/utils'
import { AppLogger } from '@/lib/logger'

const log = AppLogger.getLogger(import.meta)

export type AddResourceTypeComposerProps = {
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  initialValues?: Partial<AddResourceTypeFormData>
  showHeader?: boolean
  submitLabel?: string
}

type ResourceTypeSelectOption = { value: number; label: string }

const validateField = (name: keyof AddResourceTypeFormData, value: unknown): string | null => {
  const shape = AddResourceTypeFormSchema.shape[name]
  if (!shape) return null
  const parsed = shape.safeParse(value)
  if (!parsed.success) {
    return parsed.error.issues[0]?.message ?? 'Invalid value'
  }
  return null
}

// Resource type names must be lowercase (they form part of the resource URI).
const validateResourceTypeNameCase = (value: unknown): string | null => {
  if (typeof value === 'string' && value.trim() !== '' && value !== value.toLowerCase()) {
    return 'Name must be in lowercase letters only'
  }
  return null
}

export function AddResourceTypeComposer(props: AddResourceTypeComposerProps) {
  const { onSuccess, onError, onCancel, initialValues, showHeader = true, submitLabel } = props

  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
  const groups = FormConfigLoader.getGroups(FORM_CONFIG)

  const isRequiredField = (name: string) => {
    return ['namespace', 'resourceTypeCategory', 'resourceTypeName'].includes(name)
  }

  const [parentResourceTypes, setParentResourceTypes] = useState<number[]>(
    initialValues?.parentResourceTypes ?? []
  )
  const [environment, setEnvironment] = useState<RemaEnvironment>('development')
  const [permissions, setPermissions] = useState<string[]>(
    initialValues?.permissions ?? []
  )

  // Fetch all resource types for the parent multi-select
  const { data: resourceTypesData } = useApiQuery('getResourceTypes', {})
  const allResourceTypes: ResourceTypeSelectOption[] = (
    resourceTypesData?.content?.resourceTypes || []
  ).map((rt: { resourceTypeNumber: number; resourceTypeName: string }) => ({
    value: rt.resourceTypeNumber,
    label: rt.resourceTypeName,
  }))

  const upsertResourceType = useApiMutation('upsertResourceType', 'POST', {
    invalidates: ['getResourceTypes'],
    onSuccess: () => onSuccess?.(),
    onError: (error) => {
      log.error('Failed to create resource type:', error)
      onError?.(error)
    },
  })

  const form = useAppForm({
    schema: AddResourceTypeFormSchema,
    defaultValues: {
      resourceTypeNumber: initialValues?.resourceTypeNumber,
      namespace: initialValues?.namespace ?? ('' as any),
      resourceTypeCategory: initialValues?.resourceTypeCategory ?? ('' as any),
      resourceTypeName: initialValues?.resourceTypeName ?? '',
      resourceTypeDescription: initialValues?.resourceTypeDescription ?? '',
      parentResourceTypes: initialValues?.parentResourceTypes ?? [],
      permissions: initialValues?.permissions ?? [],
    },
    onSubmit: async ({ value }) => {
      const formValue = value as AddResourceTypeFormData
      const parsed = AddResourceTypeFormSchema.safeParse({
        resourceTypeNumber: initialValues?.resourceTypeNumber,
        namespace: formValue.namespace,
        resourceTypeCategory: formValue.resourceTypeCategory,
        resourceTypeName: formValue.resourceTypeName?.trim() ?? '',
        resourceTypeDescription: formValue.resourceTypeDescription?.trim() ?? '',
        parentResourceTypes,
        permissions,
      })

      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          const fieldName = issue.path[0]
          if (typeof fieldName === 'string') {
            form.setFieldMeta(fieldName as any, (meta) => ({
              ...meta,
              errors: [issue.message],
            }))
          }
        }
        return
      }

      // Enforce lowercase resource type name before submit.
      const nameCaseError = validateResourceTypeNameCase(parsed.data.resourceTypeName)
      if (nameCaseError) {
        form.setFieldMeta('resourceTypeName', (meta) => ({ ...meta, isTouched: true, errors: [nameCaseError] }))
        return
      }

      const apiRequest = transformToApiRequest({ ...parsed.data, parentResourceTypes, permissions })
      await upsertResourceType.mutateAsync({ ...apiRequest, resourceTypeUri: liveUri })
    },
  })

  // Compute live URI from current form state
  const formState = form.state.values as any
  const parentNames = parentResourceTypes
    .map((id) => allResourceTypes.find((rt) => rt.value === id)?.label ?? '')
    .filter(Boolean)
  const liveUri = buildResourceTypeUri({
    namespace: formState.namespace ?? '',
    tenantId: getTenantId(formState.namespace ?? '', environment),
    category: formState.resourceTypeCategory ?? '',
    parentNames,
    resourceTypeName: formState.resourceTypeName ?? '',
  })

  const renderControl = (control: any) => {
    const isRequired = isRequiredField(control.name)
    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          onBlur: ({ value }) =>
            validateField(control.name as keyof AddResourceTypeFormData, value) || undefined,
        }}
        listeners={
          control.name === 'resourceTypeName'
            ? {
                // Auto-correct the name as the user types: lowercase, and only
                // lowercase letters and hyphens are kept (everything else stripped).
                onChange: ({ value }) => {
                  const sanitized = sanitizeResourceTypeName(value)
                  if (sanitized !== value) form.setFieldValue('resourceTypeName', sanitized as any)
                },
              }
            : undefined
        }
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: isRequired,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
          }
          if (control.htmlControl === 'textarea') return renderTextareaControl(config, bound)
          if (control.htmlControl === 'select') {
            return renderSelectControl({
              ...config,
              options: control.selectOptions?.map((opt: any) => ({ value: opt.value, label: opt.label })) || [],
            }, bound)
          }
          return renderTextControl(config, bound)
        }}
      </form.AppField>
    )
  }

  const renderFormContent = () => {
    return groups.map((group) => {
      const groupControls = controlsByGroup.get(group.formGroupName) || []
      if (groupControls.length === 0) return null
      return (
        <div key={group.formGroupName} className="space-y-4">
          {groupControls.map(renderControl)}
        </div>
      )
    })
  }

  const formBody = (
    <>
      <RemaEnvironmentBar
        environment={environment}
        onEnvironmentChange={setEnvironment}
        namespace={formState.namespace}
      />

      {renderFormContent()}

      <div className="space-y-2">
        <p className="text-sm font-medium">Parent Resource Types</p>
        <p className="text-xs text-muted-foreground">Select and order the parent resource types for this type</p>
        <OrderedMultiSelect
          options={allResourceTypes}
          value={parentResourceTypes}
          onChange={setParentResourceTypes}
          placeholder="Search parent types..."
        />
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium">Permissions</p>
        <p className="text-xs text-muted-foreground">Select which permissions are allowed for this resource type</p>
        <PermissionsToggleControl value={permissions} onChange={setPermissions} />
      </div>

      <LiveUriPreview uri={liveUri} label="Resource Type URI Preview" />

      <div className="pt-4 flex justify-center gap-3">
        {renderButtonControl({
          label: FORM_CONFIG.cancelButton?.label ?? '',
          variant: 'outline',
          onClick: onCancel,
          visibility: !!onCancel,
          type: 'button'
        })}
        {renderButtonControl({
          label: upsertResourceType.isPending
            ? (FORM_CONFIG.submitButton?.loadingLabel ?? '')
            : (submitLabel ?? FORM_CONFIG.submitButton?.label ?? ''),
          variant: 'default',
          type: 'submit'
        }, { SubmitButton: form.SubmitButton })}
      </div>
    </>
  )

  return (
    <form.AppForm>
      {showHeader ? (
        <div className="space-y-4">
          <Card className="shadow-sm pb-0 overflow-hidden">
            <CardHeader className="bg-slate-700 text-white [&_p]:text-slate-300 pb-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <Settings2 className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">{FORM_CONFIG.formTitle}</CardTitle>
                  <CardDescription>{FORM_CONFIG.formDescription}</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="space-y-6 pt-6">{formBody}</CardContent>
          </Card>
        </div>
      ) : (
        <div className="space-y-6">{formBody}</div>
      )}
    </form.AppForm>
  )
}

export {
  AddResourceTypeFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type AddResourceTypeFormData
} from './schema'
